'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { Card } from '@/entities/auth'
import { useSignUpGuestStore } from '@/features/sign-up'
import { TGuestInfoForm, createZGuestInfoForm } from '@/features/sign-up'
import {
  EmailField,
  NameField,
  PasswordConfirmField,
  PasswordField,
  ProfileImageField,
} from '@/features/update-user-information'
import { useRouter } from '@/shared/i18n'
import { Slot, useDebounce } from '@/shared/lib'
import { Button, Form } from '@/shared/ui'
import { useValidateEmail } from '@/widgets/sign-up'

export const SignUpGuestInfo = () => {
  const router = useRouter()
  const { actions, info } = useSignUpGuestStore()

  const tAccount = useTranslations('account')
  const t = useTranslations('signup_information')

  const { validateEmailAsync } = useValidateEmail()

  const form = useForm<TGuestInfoForm>({
    resolver: zodResolver(createZGuestInfoForm(tAccount)),
    defaultValues: info,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const watch = useWatch({
    control: form.control,
  })

  const debouncedWatch = useDebounce(watch, 300)

  const submitForm = async (formValues: TGuestInfoForm) => {
    const isValid = await validateEmailAsync(formValues.email)

    if (!isValid) {
      return
    }

    router.push(`/sign-up/guest?step=verification`)
  }

  useEffect(() => {
    actions.updateInfo(debouncedWatch as TGuestInfoForm)
  }, [debouncedWatch, actions])

  return (
    <Card progress={50}>
      <Slot name="title">{t('title')}</Slot>
      <Slot name="children">
        <Form.Root form={form} className="space-y-6">
          <EmailField placeholder={tAccount('email_available_placeholder')} />
          <NameField />
          <PasswordField />
          <PasswordConfirmField />
          <ProfileImageField />

          <Button
            size="large"
            className="mt-10"
            type="submit"
            fullWidth
            onClick={form.handleSubmit(submitForm)}
          >
            {t('button.next')}
          </Button>
        </Form.Root>
      </Slot>
    </Card>
  )
}
